/**
 * Centralized route configuration.
 *
 * RULE: Do not hardcode route strings anywhere else in the app.
 * Always import and use `ROUTES.<key>.path`.
 */
export const ROUTES = Object.freeze({
    root: Object.freeze({ name: "root", path: "/" }),
    // Public
    login: Object.freeze({ name: "login", path: "/login" }),
    register: Object.freeze({ name: "register", path: "/register" }),
    verify: Object.freeze({ name: "verify", path: "/verify" }),
    forgotPassword: Object.freeze({ name: "forgotPassword", path: "/forgot-password" }),
    resetPassword: Object.freeze({ name: "resetPassword", path: "/reset-password" }),
    // Protected
    // Legacy/entry route (redirects to Account Summary)
    // Icon-rail pages
    home: Object.freeze({ name: "home", path: "/home" }),
    favorites: Object.freeze({ name: "favorites", path: "/favorites" }),
    settings: Object.freeze({ name: "settings", path: "/settings" }),
    moduleList: Object.freeze({ name: "moduleList", path: "/module-list" }),
    // Menu pages (Management)
    activeClientList: Object.freeze({
        name: "activeClientList",
        path: "/active-client-list",
    }),
    accountSummary: Object.freeze({
        name: "accountSummary",
        path: "/account-summary",
    }),
    divergenceReport: Object.freeze({
        name: "divergenceReport",
        path: "/divergence-report",
    }),
    accountPerformance: Object.freeze({
        name: "accountPerformance",
        path: "/account-performance",
    }),
    accountPerformanceReport: Object.freeze({
        name: "accountPerformanceReport",
        path: "/account-performance-report",
    }),
    ltvReport: Object.freeze({ name: "ltvReport", path: "/ltv-report" }),
    commissionTracker: Object.freeze({ name: "commissionTracker", path: "/commission-tracker" }),
    commissionTrackerReport: Object.freeze({ name: "commissionTrackerReport", path: "/commission-tracker-report" }),
    // Path must match the `url` on the billing_summary module row so the sidebar
    // item highlights on an exact pathname match.
    billingSummary: Object.freeze({ name: "billingSummary", path: "/billing-summary" }),
    emailMarketingReport: Object.freeze({ name: "emailMarketingReport", path: "/email-marketing" }),
    marketingCalender: Object.freeze({ name: "marketingCalender", path: "/marketing-calendar" }),
    adApprovals: Object.freeze({ name: "adApprovals", path: "/ad-approvals" }),
    // User Management
    userList: Object.freeze({ name: "userList", path: "/user-list" }),
    rolesPermission: Object.freeze({ name: "rolesPermission", path: "/roles-permissions" }),
    rolePermissionCreate: Object.freeze({ name: "rolePermissionCreate", path: "/role-permission-create" }),
    rolePermissionEdit: Object.freeze({ name: "rolePermissionEdit", path: "/role-permission-edit/:id" }),
    profile: Object.freeze({ name: "profile", path: "/profile" }),
    activeClient: Object.freeze({ name: "active_clients", path: "/active-clients" }),
    clients: Object.freeze({ name: "clients", path: "/clients" }),
    proTeamLeaguePerformance: Object.freeze({ name: "proTeamLeaguePerformance", path: "/pro-team-league-performance" }),
    metaAdAudit: Object.freeze({
        name: "metaAdAudit",
        path: "/meta-ad-audit",
    }),
    metaAdAuditDetail: Object.freeze({
        name: "metaAdAuditDetail",
        path: "/meta-ad-audit/report/:reportId",
    }),
    meetingAgenda: Object.freeze({
        name: "meetingAgenda",
        path: "/meeting-agenda",
    }),
    meetingScorer: Object.freeze({
        name: "meetingScorer",
        path: "/meeting-scorer",
    }),
    // Opened from the Meeting Scorer action emails (details needed / transcript missing).
    meetingScorerAction: Object.freeze({
        name: "meetingScorerAction",
        path: "/meeting-scorer/action/:id",
    }),
    adTesting: Object.freeze({
        name: "adTesting",
        path: "/ad-testing",
    }),
    // Fallback
    notFound: Object.freeze({ name: "notFound", path: "/notfound" }),
    holidaySettings: Object.freeze({ name: "holidaySettings", path: "/holiday-calendar" }),
    optedOutContacts: Object.freeze({ name: "optedOutContacts", path: "/optout-contacts" }),
    createDirectory: Object.freeze({ name: "createDirectory", path: "/creator-directory" }),
    partnerDirectory: Object.freeze({ name: "partnerDirectory", path: "/partner-directory" }),
    contentOpportunities: Object.freeze({ name: "contentOpportunities", path: "/content-opportunities" }),
    clientGoal: Object.freeze({ name: "clientGoal", path: "/client-goal" }),
    emailConfiguration: Object.freeze({ name: "emailConfiguration", path: "/email-configuration" }),
    syncLog: Object.freeze({ name: "syncLog", path: "/sync-log" }),
    affiliateAccountPerformance: Object.freeze({ name: "affiliateAccountPerformance", path: "/affiliate-account-performance" }),
    clickImport: Object.freeze({ name: "clickImport", path: "/click-import" }),
    publisherTagging: Object.freeze({ name: "publisherTagging", path: "/publisher-tagging" }),
    publisherReporting: Object.freeze({ name: "publisherReporting", path: "/publisher-reporting" }),
    integrationCredentials: Object.freeze({ name: "integrationCredentials", path: "/integration-credentials" }),
    accountOverview: Object.freeze({ name: "accountOverview", path: "/account-overview" }),
    addOutreachTracker: Object.freeze({
        name: "addOutreachTracker",
        path: "/add-outreach-tracker"
    }),
    viewOutreachTracker: Object.freeze({
        name: "viewOutreachTracker",
        path: "/outreach-tracker/:id"
    }),
    viewSocialYtTracker: Object.freeze({
        name: "viewSocialYtTracker",
        path: "/social-yt-tracker/:id"
    }),
    socialYtTracker: Object.freeze({
        name: "socialYtTracker",
        path: "/social-yt-tracker"
    }),
    outreachTracker: Object.freeze({
        name: "outreachTracker",
        path: "/outreach-trackers"
    }),
    semrushTool: Object.freeze({ name: "semrushTool", path: "/semrush-tool" }),
    linkBuilderTool: Object.freeze({ name: "linkBuilderTool", path: "/link-builder-tool" }),
    salesGoal: Object.freeze({ name: "salesGoal", path: "/sales-goal" }),
    salesReporting: Object.freeze({ name: "salesReporting", path: "/sales-reporting" }),
    monthlyProjection: Object.freeze({ name: "monthlyProjection", path: "/monthly-projection" }),
    clockifyGoal: Object.freeze({ name: "clockifyGoal", path: "/clockify-goal" }),
    shopifyConnectionComplete: Object.freeze({ name: "shopifyConnectionComplete", path: "/shopify-connection-complete" }),
    accessRestricted: Object.freeze({ name: "accessRestricted", path: "/access-restricted" }),
    // Path matches the `url` on the employee_performance module row (modules.json).
    employeePerformance: Object.freeze({ name: "employeePerformance", path: "/employee-performance" }),
    employeePerformanceDetails: Object.freeze({ name: "employeePerformanceDetails", path: "/employee-performance-details" }),
    outreachPerformance: Object.freeze({ name: "outreachPerformance", path: "/outreach-performance" }),
    // Path matches the `url` on the create_report module row (Report Builder →
    // Create Report) so the sidebar item highlights on an exact pathname match.
    createReport: Object.freeze({ name: "createReport", path: "/create-report" }),
    // Add / Edit / View sub-pages. Registered in MODULE_DESCRIPTORS so the
    // sidebar stays on Create Report and the header keeps the module title.
    createReportAdd: Object.freeze({ name: "createReportAdd", path: "/create-report/add" }),
    createReportEdit: Object.freeze({ name: "createReportEdit", path: "/create-report/edit/:reportId" }),
    createReportView: Object.freeze({ name: "createReportView", path: "/create-report/view/:reportId" }),
});
